import React, { useState, useEffect } from 'react';
import { extractEmployeeMentions } from '../utils/openai';
import { Trophy, Medal, Award, Star, TrendingUp, Calendar, Users, Search, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const formatNumber = (num) => {
  return num.toLocaleString();
};

const PeakPerformers = ({ reviews }) => {
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('30days');
  const [currentReviewIndex, setCurrentReviewIndex] = useState({});

  const segments = [
    'General Hotel',
    'Ski Mountain', 
    'Aquatopia',
    'Camelbeach',
    'Mountain Adventures'
  ];

  const getTimeframeCutoff = (timeframe) => {
    const now = new Date();
    switch (timeframe) {
      case '30days':
        return new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      case '3months':
        return new Date(now.setMonth(now.getMonth() - 3));
      case '6months':
        return new Date(now.setMonth(now.getMonth() - 6));
      case 'all':
      default:
        return new Date(now.setFullYear(now.getFullYear() - 1));
    }
  };

  const generatePeakPerformers = async () => {
    if (reviews.length === 0) {
      setError('No reviews available for analysis');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Filter reviews based on selected timeframe and positive ratings
      const cutoffDate = getTimeframeCutoff(selectedTimeframe);
      
      const filteredReviews = reviews.filter(review => 
        review.RATING >= 4 && 
        new Date(review.DATE) >= cutoffDate &&
        (selectedSegment === 'all' || review.SEGMENT === selectedSegment)
      );

      if (filteredReviews.length === 0) {
        setError(`No positive reviews found in the selected timeframe${selectedSegment !== 'all' ? ' and segment' : ''}`);
        return;
      }

      const result = await extractEmployeeMentions(filteredReviews);
      setEmployeeData(result.employeeData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Remove automatic loading - let user trigger it manually
  useEffect(() => {
    // Only regenerate if we already have data and filters change
    if (reviews.length > 0 && employeeData.length > 0) {
      generatePeakPerformers();
    }
  }, [selectedTimeframe, selectedSegment]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredEmployees = () => {
    // Since AI extraction already handles timeframe and segment filtering,
    // we just need to return all sorted data
    return [...employeeData]
      .sort((a, b) => b.totalMentions - a.totalMentions);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="rank-icon gold" size={24} />;
      case 2:
        return <Medal className="rank-icon silver" size={24} />;
      case 3:
        return <Award className="rank-icon bronze" size={24} />;
      default:
        return <Star className="rank-icon default" size={20} />;
    }
  };

  const getSegmentColor = (segment) => {
    const colors = {
      'General Hotel': '#2563eb',
      'Ski Mountain': '#dc2626', 
      'Aquatopia': '#0891b2',
      'Camelbeach': '#ea580c',
      'Mountain Adventures': '#16a34a'
    };
    return colors[segment] || '#6b7280';
  };

  const navigateReview = (employeeKey, direction) => {
    const employee = employeeData.find(emp => `${emp.name}_${emp.segment}` === employeeKey);
    if (!employee || !employee.reviews) return;

    const currentIndex = currentReviewIndex[employeeKey] || 0;
    let newIndex;

    if (direction === 'next') {
      newIndex = (currentIndex + 1) % employee.reviews.length;
    } else {
      newIndex = currentIndex === 0 ? employee.reviews.length - 1 : currentIndex - 1;
    }

    setCurrentReviewIndex({
      ...currentReviewIndex,
      [employeeKey]: newIndex
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredEmployees = getFilteredEmployees();

  if (loading) {
    return (
      <div className="peak-performers">
        <div className="performers-header">
          <div>
            <h2><Trophy size={24} /> Peak Performers</h2>
            <p className="data-scope">Analyzing positive mentions from {formatNumber(reviews.length)} reviews</p>
          </div>
        </div>
        <div className="loading-performers">
          <div className="loading-spinner"></div>
          <p>Extracting employee mentions with AI...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="peak-performers">
        <div className="performers-header">
          <div>
            <h2><Trophy size={24} /> Peak Performers</h2>
          </div>
          <button className="refresh-btn" onClick={generatePeakPerformers}>
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      </div>
    );
  }

  if (employeeData.length === 0) {
    return (
      <div className="peak-performers">
        <div className="performers-header">
          <div>
            <h2><Trophy size={24} /> Peak Performers</h2>
            <p className="data-scope">Extract employee mentions from positive reviews</p>
          </div>
          <button className="generate-btn" onClick={generatePeakPerformers}>
            <Search size={16} />
            Find Peak Performers
          </button>
        </div>
        <div className="no-data">
          <p>Click "Find Peak Performers" to identify employees mentioned in positive reviews.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="peak-performers">
      <div className="performers-header">
        <div>
          <h2><Trophy size={24} /> Peak Performers</h2>
          <p className="data-scope">
            {filteredEmployees.length} top performers • {
              selectedTimeframe === '30days' ? 'Last 30 days' :
              selectedTimeframe === '3months' ? 'Last 3 months' :
              selectedTimeframe === '6months' ? 'Last 6 months' :
              'Last 12 months'
            } • 4-5 star reviews only
          </p>
          {reviews.length < 100 && (
            <p className="filter-notice"><Search size={14} /> Filtered data - adjust filters above to change scope</p>
          )}
        </div>
        <button className="refresh-btn" onClick={generatePeakPerformers}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="performers-filters">
        <div className="filter-group">
          <label>Business Segment</label>
          <select 
            value={selectedSegment}
            onChange={(e) => setSelectedSegment(e.target.value)}
          >
            <option value="all">All Segments</option>
            {segments.map(segment => (
              <option key={segment} value={segment}>
                {segment}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Timeframe</label>
          <select 
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
          >
            <option value="30days">Last 30 Days</option>
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="all">Last 12 Months</option>
          </select>
        </div>
      </div>


      {/* Leaderboard */}
      <div className="leaderboard">
        <div className="leaderboard-header">
          <h3><Users size={20} /> Top Performers Leaderboard</h3>
          <div className="leaderboard-stats">
            <span>{filteredEmployees.length} employees recognized</span>
            <span>•</span>
            <span>{filteredEmployees.reduce((sum, emp) => sum + emp.totalMentions, 0)} total mentions</span>
          </div>
        </div>

        <div className="leaderboard-list">
          {filteredEmployees.map((employee, index) => {
            const employeeKey = `${employee.name}_${employee.segment}`;
            const currentIndex = currentReviewIndex[employeeKey] || 0;
            const currentReview = employee.reviews?.[currentIndex];
            
            return (
              <div key={employeeKey} className={`performer-card rank-${index + 1}`}>
                <div className="performer-rank">
                  {getRankIcon(index + 1)}
                  <span className="rank-number">#{index + 1}</span>
                </div>
                
                <div className="performer-info">
                  <div className="performer-header">
                    <h4 className="performer-name">{employee.name}</h4>
                    <span 
                      className="performer-segment"
                      style={{ backgroundColor: getSegmentColor(employee.segment) }}
                    >
                      {employee.segment}
                    </span>
                  </div>
                  
                  <div className="performer-stats">
                    <div className="stat-item">
                      <Star size={16} />
                      <span><strong>{employee.totalMentions}</strong> mentions</span>
                    </div>
                    <div className="stat-item">
                      <Calendar size={16} />
                      <span><strong>{employee.avgMonthlyMentions.toFixed(1)}</strong> avg/month</span>
                    </div>
                    <div className="stat-item">
                      <TrendingUp size={16} />
                      <span><strong>{Object.keys(employee.monthlyMentions).length}</strong> active months</span>
                    </div>
                  </div>

                  {employee.positiveActions.length > 0 && (
                    <div className="positive-actions">
                      <strong>Recognized for:</strong>
                      <div className="actions-tags">
                        {employee.positiveActions.slice(0, 3).map((action, actionIndex) => (
                          <span key={actionIndex} className="action-tag">
                            {action}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {employee.reviews && employee.reviews.length > 0 && currentReview && (
                    <div className="review-carousel">
                      <div className="carousel-header">
                        <div className="review-date">
                          <Calendar size={14} />
                          <span>{formatDate(currentReview.date)}</span>
                        </div>
                        {employee.reviews.length > 1 && (
                          <div className="carousel-controls">
                            <button 
                              className="carousel-btn"
                              onClick={() => navigateReview(employeeKey, 'prev')}
                            >
                              <ChevronLeft size={14} />
                            </button>
                            <span className="review-counter">
                              {currentIndex + 1}/{employee.reviews.length}
                            </span>
                            <button 
                              className="carousel-btn"
                              onClick={() => navigateReview(employeeKey, 'next')}
                            >
                              <ChevronRight size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="review-excerpt">
                        <em>"{currentReview.excerpt}"</em>
                      </div>
                    </div>
                  )}
                </div>

                <div className="performer-trend">
                  <div className="mini-chart">
                    {Object.entries(employee.monthlyMentions)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .slice(-6)
                      .map(([month, mentions]) => (
                        <div 
                          key={month}
                          className="trend-bar"
                          style={{ 
                            height: `${Math.max((mentions / Math.max(...Object.values(employee.monthlyMentions))) * 100, 10)}%` 
                          }}
                          title={`${month}: ${mentions} mentions`}
                        />
                      ))
                    }
                  </div>
                  <span className="trend-label">Last 6 months</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PeakPerformers;